import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';
import { useAuth } from './AuthContext';
import type { MyTenant } from '../hooks/useMyTenant';

export interface TenantSummary {
  id: string;
  name: string;
  shortId: string;
  slug: string | null;
  role: string;
  isPersonal: boolean;
  projectCount: number;
  /** APPROVE.1a (#3183) — access granted through a partner link. */
  viaPartner?: boolean;
}

interface TenantContextValue {
  currentTenant: TenantSummary | null;
  availableTenants: TenantSummary[];
  isLoading: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  /**
   * WS.C1 — the raw /tenants/me/membership response, exposed so
   * useMyTenant() can shim its historical return shape without a second
   * independent fetch. Not part of the public switcher-facing contract.
   */
  myTenant: MyTenant | null;
  myTenantLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface RawTenantEntry {
  id: string;
  name: string;
  slug: string | null;
  role: string;
  isPersonal: boolean;
  projectCount: number;
  viaPartner?: boolean;
}

function toSummary(raw: RawTenantEntry): TenantSummary {
  return {
    id: raw.id,
    name: raw.name,
    shortId: raw.id.slice(0, 8),
    slug: raw.slug ?? null,
    role: raw.role,
    isPersonal: raw.isPersonal,
    projectCount: raw.projectCount,
    viaPartner: raw.viaPartner === true,
  };
}

/**
 * WS.B1 pre-deploy fallback (GET /auth/tenants 404s) — derives a single-entry
 * TenantSummary from /tenants/me/membership. isPersonal/projectCount aren't
 * carried by that endpoint, so they default rather than guess.
 */
function summaryFromMyTenant(mt: MyTenant): TenantSummary {
  return {
    id: mt.tenantId,
    name: mt.tenantName,
    shortId: mt.tenantId.slice(0, 8),
    slug: null,
    role: mt.role,
    isPersonal: false,
    projectCount: 0,
  };
}

function decodeTenantIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload<{ tenantId?: unknown }>(token);
  return typeof payload?.tenantId === 'string' ? payload.tenantId : null;
}

// Fetched state is tagged with the token it was fetched for, so the public
// view can be DERIVED from `token` + the tagged result instead of resetting
// via a synchronous setState call in the effect body (which
// react-hooks/set-state-in-effect correctly flags as a cascading-render
// hazard — same idiom as useProjectPhases.ts). All setState calls below
// happen only inside .then()/.catch() promise callbacks.
interface MyTenantResult {
  token: string;
  myTenant: MyTenant | null;
}

type TenantsResult =
  | { token: string; kind: 'ok'; tenants: TenantSummary[] }
  | { token: string; kind: 'fallback' }
  | { token: string; kind: 'error' };

export function TenantProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();

  const [myTenantResult, setMyTenantResult] = useState<MyTenantResult | null>(null);
  const [tenantsResult, setTenantsResult] = useState<TenantsResult | null>(null);

  // Always fetch /tenants/me/membership — this is the sole data source for
  // the useMyTenant() shim, independent of whether GET /auth/tenants exists.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiFetch<MyTenant>('/tenants/me/membership')
      .then((mt) => { if (!cancelled) setMyTenantResult({ token, myTenant: mt }); })
      .catch(() => { if (!cancelled) setMyTenantResult({ token, myTenant: null }); });
    return () => { cancelled = true; };
  }, [token]);

  // Attempt GET /auth/tenants for the switcher's availableTenants[]. A 404
  // (WS.B1 not yet deployed) is handled by deriving from myTenant below
  // instead of issuing a second /tenants/me/membership call.
  //
  // #1674 — the endpoint returns { tenants: [...] } (auth.ts:707), not a
  // bare array. The previous bare-array parse threw `entries.map is not a
  // function` on every real response, caught below as a generic (non-404)
  // error — which silently emptied availableTenants and nulled
  // currentTenant, hiding the switcher entirely for every user. Destructure
  // the actual envelope instead of assuming the bare-array shape.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiFetch<{ tenants: RawTenantEntry[] }>('/auth/tenants')
      .then(({ tenants }) => {
        if (cancelled) return;
        setTenantsResult({ token, kind: 'ok', tenants: tenants.map(toSummary) });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setTenantsResult({ token, kind: 'fallback' });
        } else {
          setTenantsResult({ token, kind: 'error' });
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // Derived views — "live" only when the tagged result matches the current
  // token, so login/logout naturally clears stale data without a reset effect.
  const myTenant = token !== null && myTenantResult?.token === token ? myTenantResult.myTenant : null;
  const myTenantLoading = token !== null && myTenantResult?.token !== token;

  const liveTenantsResult = token !== null && tenantsResult?.token === token ? tenantsResult : null;

  const availableTenants = useMemo<TenantSummary[]>(() => {
    if (liveTenantsResult?.kind === 'ok') return liveTenantsResult.tenants;
    if (liveTenantsResult?.kind === 'fallback' && myTenant) return [summaryFromMyTenant(myTenant)];
    return [];
  }, [liveTenantsResult, myTenant]);

  const currentTenant = useMemo<TenantSummary | null>(() => {
    if (liveTenantsResult?.kind === 'ok') {
      const jwtTenantId = token ? decodeTenantIdFromToken(token) : null;
      return liveTenantsResult.tenants.find((t) => t.id === jwtTenantId) ?? null;
    }
    if (liveTenantsResult?.kind === 'fallback' && myTenant) return summaryFromMyTenant(myTenant);
    return null;
  }, [liveTenantsResult, myTenant, token]);

  // Tracks availableTenants/currentTenant (switcher) readiness only — NOT
  // myTenant's independent load. On a non-404 /auth/tenants failure this
  // goes false immediately even if myTenantLoading is still true, since
  // availableTenants/currentTenant have already reached their terminal
  // ([]/null) state; useMyTenant() consumers read myTenantLoading directly
  // and never go through this flag.
  // While falling back, availableTenants/currentTenant also depend on
  // myTenant — stay loading until that fetch settles too, or the switcher
  // briefly renders "no tenants" before the fallback data arrives.
  const isLoading = token !== null
    && (liveTenantsResult === null || (liveTenantsResult.kind === 'fallback' && myTenantLoading));

  const switchTenant = useCallback(async (tenantId: string) => {
    const res = await apiFetch<{ token: string }>('/auth/switch-tenant', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    });
    localStorage.setItem('blueprint_token', res.token);
    window.location.reload();
  }, []);

  const value = useMemo<TenantContextValue>(() => ({
    currentTenant,
    availableTenants,
    isLoading,
    switchTenant,
    myTenant,
    myTenantLoading,
  }), [currentTenant, availableTenants, isLoading, switchTenant, myTenant, myTenantLoading]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Non-throwing accessor. useMyTenant() uses this (not useTenantContext) so it
 * degrades to a standalone fetch — rather than crashing — when rendered
 * without a TenantProvider ancestor, e.g. the many existing component tests
 * that mount an ArtefactDetailContent/Sidebar/etc. consumer in isolation.
 */
export function useOptionalTenantContext(): TenantContextValue | null {
  return useContext(TenantContext);
}

export function useTenantContext(): TenantContextValue {
  const ctx = useOptionalTenantContext();
  if (!ctx) throw new Error('useTenantContext must be used within TenantProvider');
  return ctx;
}
